import { supabase } from '@foundation/utils/supabaseClient';
import { getActiveWorkspaceId } from '../../../header/activeWorkspace';
import type {
  PolicyTeableRecord,
  PolicyFlatRecord,
  PolicyRecordsResponse,
} from '../types/policyTeable.types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const REVISR_BASE_NAME = 'Revisr';
const POLICIES_TABLE_NAME = 'Policies';

// Teable field names matching the Policies table
const FIELD = {
  POLICY_NAME: 'Policy Name',
  STATUS: 'Status',
  WORKFLOW_TYPE: 'Workflow Type',
  CURRENT_STEP: 'Current Step',
  ORG_ID: 'Org ID',
  USER_ID: 'User ID',
  FILEPATH: 'Markdown URL',
  POLICY_MARKDOWN_URL: 'Policy Markdown URL',
  JSON_CHANGES_URL: 'JSON Changes URL',
  TEMPLATE_STORE_ID: 'Template Store ID',
  POLICY_STORE_ID: 'Policy Store ID',
  STANDARD_STORE_ID: 'Standard Store ID',
  GENERATION_PROMPT: 'Generation Prompt',
  AUDIT_RESULTS: 'Audit Results',
  BRANDING_DATA: 'Branding Data',
  CREATED_AT: 'Created At',
  UPDATED_AT: 'Updated At',
} as const;

const FIELD_TO_PROP: Record<string, string> = {
  [FIELD.POLICY_NAME]: 'policy_name',
  [FIELD.STATUS]: 'status',
  [FIELD.WORKFLOW_TYPE]: 'workflow_type',
  [FIELD.CURRENT_STEP]: 'current_step',
  [FIELD.ORG_ID]: 'org_id',
  [FIELD.USER_ID]: 'user_id',
  [FIELD.FILEPATH]: 'markdown_url',
  [FIELD.POLICY_MARKDOWN_URL]: 'policy_markdown_url',
  [FIELD.JSON_CHANGES_URL]: 'json_changes_url',
  [FIELD.TEMPLATE_STORE_ID]: 'template_store_id',
  [FIELD.POLICY_STORE_ID]: 'policy_store_id',
  [FIELD.STANDARD_STORE_ID]: 'standard_store_id',
  [FIELD.GENERATION_PROMPT]: 'generation_prompt',
  [FIELD.AUDIT_RESULTS]: 'audit_results',
  [FIELD.BRANDING_DATA]: 'branding_data',
  [FIELD.CREATED_AT]: 'created_at',
  [FIELD.UPDATED_AT]: 'updated_at',
};

const JSON_FIELDS = new Set(['audit_results', 'branding_data']);

const getHeaders = (): Record<string, string> => ({
  'Content-Type': 'application/json',
});

// In-memory cache
let cachedTableId: string | null = null;
let cachedTenantId: string | null = null;

function fromTeableRecord(record: PolicyTeableRecord): PolicyFlatRecord {
  const result: PolicyFlatRecord = { id: record.id };

  for (const [fieldName, value] of Object.entries(record.fields)) {
    const prop = FIELD_TO_PROP[fieldName];
    if (!prop) continue;

    if (JSON_FIELDS.has(prop) && typeof value === 'string') {
      try {
        (result as Record<string, unknown>)[prop] = JSON.parse(value);
      } catch {
        (result as Record<string, unknown>)[prop] = value;
      }
    } else {
      (result as Record<string, unknown>)[prop] = value;
    }
  }

  // System fields as fallback — used when explicit 'Created At'/'Updated At'
  // field types haven't been added to the table yet
  if (!result.created_at && record.createdTime) {
    result.created_at = record.createdTime;
  }
  if (!result.updated_at && record.lastModifiedTime) {
    result.updated_at = record.lastModifiedTime;
  }

  return result;
}

const resolveTableId = async (): Promise<string> => {
  const tenantId = getActiveWorkspaceId();
  if (!tenantId) {
    throw new Error('No active workspace selected. Please select a workspace first.');
  }

  // Return cached value if same tenant
  if (cachedTableId && cachedTenantId === tenantId) return cachedTableId;

  const { data: tenant, error } = await supabase
    .schema('altzero')
    .from('base_tenants')
    .select('bases')
    .eq('tenant_id', tenantId)
    .single();

  if (error || !tenant) {
    throw new Error('Failed to load workspace configuration.');
  }

  const bases = tenant.bases as Record<string, string> | null;
  const baseId = bases?.[REVISR_BASE_NAME];
  if (!baseId) {
    throw new Error(
      `Revisr base "${REVISR_BASE_NAME}" not found for this workspace. Please complete onboarding first.`,
    );
  }

  const tablesRes = await fetch(
    `${API_BASE_URL}/api/teable/proxy/base/${baseId}/table`,
    { headers: getHeaders() },
  );

  if (!tablesRes.ok) {
    throw new Error(`Failed to list tables in Revisr base: ${tablesRes.status}`);
  }

  const body = await tablesRes.json();
  const tables: { id: string; name: string }[] = Array.isArray(body) ? body : (body.tables ?? []);

  const policiesTable = tables.find((t) => t.name === POLICIES_TABLE_NAME);
  if (!policiesTable) {
    throw new Error(`"${POLICIES_TABLE_NAME}" table not found in Revisr base.`);
  }

  cachedTableId = policiesTable.id;
  cachedTenantId = tenantId;
  return cachedTableId;
};

export const clearPolicyTableCache = () => {
  cachedTableId = null;
  cachedTenantId = null;
};

export const policyTeableService = {
  // Count total policy records for the active workspace in Teable.
  // Uses take=1 to minimise payload — only the `total` field is needed.
  getPolicyCount: async (): Promise<number> => {
    const tableId = await resolveTableId();
    const response = await fetch(
      `${API_BASE_URL}/api/teable/proxy/table/${tableId}/record?take=1`,
      { headers: getHeaders() },
    );

    if (!response.ok) {
      throw new Error(`Failed to count policies: ${response.status}`);
    }

    const data = await response.json();
    // Teable returns { records: [...], total: N } — fall back to records.length if total absent
    return typeof data.total === 'number' ? data.total : (data.records?.length ?? 0);
  },

  // Fetch all policy records for the active workspace via the backend API.
  // Uses the server-side Teable connection (same path as save) so it never
  // fails due to proxy auth or client-side resolveTableId issues.
  getPolicies: async (_userId?: string, orgId?: string): Promise<PolicyFlatRecord[]> => {
    const effectiveOrgId = orgId ?? getActiveWorkspaceId();
    if (!effectiveOrgId) {
      throw new Error('No active workspace selected. Please select a workspace first.');
    }

    const response = await fetch(
      `${API_BASE_URL}/api/revisr/policy-generation`,
      {
        headers: {
          'Content-Type': 'application/json',
          'x-org-id': effectiveOrgId,
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch policies: ${response.status}`);
    }

    const data = await response.json() as { records: Record<string, unknown>[] };
    return (data.records ?? []) as PolicyFlatRecord[];
  },

  // Fetch a single policy record by ID
  getPolicy: async (recordId: string): Promise<PolicyFlatRecord | null> => {
    const tableId = await resolveTableId();
    const response = await fetch(
      `${API_BASE_URL}/api/teable/proxy/table/${tableId}/record/${recordId}`,
      { headers: getHeaders() },
    );

    if (response.status === 404) return null;

    if (!response.ok) {
      throw new Error(`Failed to fetch policy: ${response.status}`);
    }

    const record: PolicyTeableRecord = await response.json();
    return fromTeableRecord(record);
  },

  // Delete a policy record
  deletePolicy: async (recordId: string): Promise<void> => {
    const tableId = await resolveTableId();
    const response = await fetch(
      `${API_BASE_URL}/api/teable/proxy/table/${tableId}/record/${recordId}`,
      {
        method: 'DELETE',
        headers: getHeaders(),
      },
    );

    if (!response.ok && response.status !== 404) {
      throw new Error(`Failed to delete policy: ${response.status}`);
    }
  },
};
